import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineUserGroup,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineLockClosed,
  HiOutlineShieldCheck
} from 'react-icons/hi2';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services';
import masterDataService from '../../services/masterDataService';
import { toast } from 'react-toastify';
import SharedModal from '../../components/common/SharedModal';
import PageLoading from '../../components/common/PageLoading';

import './Roles.css';

const Roles = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    roleName: '',
    description: '',
    status: 'Active',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadRoles();
    } else {
      setError('Please login to view roles');
      setLoading(false);
    }
  }, [tenantId]);

  const loadRoles = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await masterDataService.getRoles(tenantId);
      setRoles(data || []);
    } catch (err) {
      setError('Failed to load roles. Please try again.');
      console.error('Error loading roles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredRoles = (roles || []).filter((role) =>
    role.roleName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingRole(null);
    setFormData({
      roleName: '',
      description: '',
      status: 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      roleName: role.roleName || '',
      description: role.description || '',
      status: role.status || 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deleteRole(id);
      await loadRoles();
      setDeleteConfirm(null);
      toast.success('Role deleted successfully!');
    } catch (err) {
      const msg = 'Failed to delete role. Please try again.';
      setError(msg);
      toast.error(msg);
      console.error('Error deleting role:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.roleName.trim()) {
      errors.roleName = 'Role name is required';
    } else if (formData.roleName.length > 50) {
      errors.roleName = 'Role name must be less than 50 characters';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const roleData = {
        tenantId: tenantId,
        roleName: formData.roleName.trim(),
        description: formData.description.trim() || null,
        status: formData.status,
      };

      if (editingRole) {
        roleData.roleId = editingRole.roleId;
        await masterDataService.updateRole(editingRole.roleId, roleData);
      } else {
        await masterDataService.createRole(roleData);
      }

      await loadRoles();
      handleCloseModal();
      toast.success(editingRole ? 'Role updated successfully!' : 'Role created successfully!');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.message || 
                          'Failed to save role. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRole(null);
    setFormData({ roleName: '', description: '', status: 'Active' });
    setFormErrors({});
  };

  if (loading) {
    return <PageLoading message="Loading roles..." />;
  }

  return (
    <div className="pagecontainer roles-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Role Management</h1>
          <p>Define user roles and system access policies</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleAdd}>
            <HiOutlinePlus />
            Add New Role
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container pad10">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search roles..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="table-info">
            {filteredRoles.length} {filteredRoles.length === 1 ? 'role' : 'roles'}
          </div>
        </div>

        {filteredRoles.length === 0 ? (
          <div className="empty-state">
            <HiOutlineUserGroup className="empty-icon" />
            <h3>No roles found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first user role'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                <HiOutlinePlus /> Create Role
              </button>
            )}
          </div>
        ) : (
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{width: '80px', textAlign: 'center'}}>Actions</th>
                  <th>Role Name</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th style={{textAlign: 'center'}}>Permissions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRoles.map((role) => (
                  <tr key={role.roleId}>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon-edit"
                          onClick={() => handleEdit(role)}
                          title="Edit Basic Details"
                        >
                          <HiOutlinePencil />
                        </button>
                        <button
                          className="btn-icon-delete"
                          onClick={() => setDeleteConfirm(role)}
                          title="Delete Role"
                        >
                          <HiOutlineTrash />
                        </button>
                      </div>
                    </td>
                    <td>
                      <span className="name-cell">{role.roleName}</span>
                    </td>
                    <td>
                      <span className="description-cell">{role.description || '-'}</span>
                    </td>
                    <td>
                      <span className={`status-badge ${role.status?.toLowerCase()}`}>
                        {role.status || 'Active'}
                      </span>
                    </td>
                    <td>
                      {role.createdAt
                        ? new Date(role.createdAt).toLocaleDateString()
                        : '-'}
                    </td>
                    <td>
                      <div style={{display: 'flex', justifyContent: 'center'}}>
                        <button
                          className="btn-secondary"
                          style={{padding: '6px 12px', fontSize: '12px', gap: '6px'}}
                          onClick={() => navigate(`/home/settings/permissions?roleId=${role.roleId}`)}
                        >
                          <HiOutlineLockClosed size={14}/>
                          Manage
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingRole ? 'Edit Role' : 'New Role Definition'}
        footer={
          <>
            <button className="btn-secondary" onClick={handleCloseModal} disabled={submitting}>Cancel</button>
            <button 
              type="submit" 
              form="role-form" 
              className="btn-primary" 
              disabled={submitting}
            >
              {submitting ? 'Processing...' : (editingRole ? 'Save Changes' : 'Create Role')}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form onSubmit={handleSubmit} id="role-form">
            <div className="form-section">
              <div className="form-group">
                <label htmlFor="roleName">ROLE NAME <span className="required">*</span></label>
                <input
                  type="text"
                  id="roleName"
                  value={formData.roleName}
                  onChange={(e) => setFormData({ ...formData, roleName: e.target.value })}
                  className={formErrors.roleName ? 'error' : ''}
                  placeholder="e.g. Administrator, Supervisor"
                  autoFocus
                />
                {formErrors.roleName && <span className="field-error">{formErrors.roleName}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="description">DESCRIPTION</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter role purpose and responsibilities..."
                  rows="4"
                />
              </div>

              <div className="form-group">
                <label htmlFor="status">VISIBILITY STATUS</label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="Active">Active (Visible in user lookup)</option>
                  <option value="Inactive">Inactive (Legacy/Hidden)</option>
                </select>
              </div>
            </div>

            {editingRole && (
               <div className="empty-state" style={{padding: '20px', borderStyle: 'solid', borderWidth: '1px', background: '#f8fafc'}}>
                  <p style={{fontSize: '13px', textAlign: 'center', margin: 0}}>
                    Note: To edit fine-grained page permissions for this role, use the <strong>Manage Permissions</strong> button in the table.
                  </p>
               </div>
            )}
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Role?"
        customWidth="400px"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setDeleteConfirm(null)}>Keep Role</button>
            <button className="btn-danger" onClick={() => handleDelete(deleteConfirm.roleId)}>Confirm Delete</button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>Are you sure you want to delete the <strong>{deleteConfirm?.roleName}</strong> role?</p>
          <p style={{color: '#ef4444', fontSize: '13px', marginTop: '12px', fontWeight: 600}}>
            Warning: Users assigned to this role may lose access to critical system features.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default Roles;
